import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authApi } from "../../services/api";

export const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ============================================
  // TanStack Query - Reset Password Mutation
  // ============================================
  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }) => authApi.resetPassword(token, password),
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message || t("errors.generic");
      setServerError(message);
    },
  });

  const loading = resetPasswordMutation.isPending;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = t("auth.validation.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("auth.validation.passwordMinLength", { min: 8 });
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("auth.validation.passwordsNoMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setServerError("");

    if (!validate()) return;

    resetPasswordMutation.mutate({ token, password: formData.password });
  };

  if (!token) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("auth.verifyEmail.errorTitle")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("auth.verifyEmail.errorMessage")}
        </p>
        <Link
          to="/forgot-password"
          className="text-primary hover:text-primary/80 font-medium"
        >
          {t("auth.forgotPassword.sendAnother")}
        </Link>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("auth.resetPassword.successMessage")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("auth.resetPassword.successMessage")}
        </p>
        <Button onClick={() => navigate("/login")}>
          {t("auth.login.signIn")}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground text-center mb-2">
        {t("auth.resetPassword.title")}
      </h2>
      <p className="text-muted-foreground text-center mb-6">
        {t("auth.resetPassword.subtitle")}
      </p>

      {serverError && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {serverError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">
            {t("auth.resetPassword.newPasswordLabel")}
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={t("auth.resetPassword.newPasswordPlaceholder")}
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              autoComplete="new-password"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
              aria-label={
                showPassword
                  ? t("auth.login.hidePassword")
                  : t("auth.login.showPassword")
              }
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">
            {t("auth.resetPassword.confirmPasswordLabel")}
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              autoComplete="new-password"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
              aria-label={
                showConfirmPassword
                  ? t("auth.login.hidePassword")
                  : t("auth.login.showPassword")
              }
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">{errors.confirmPassword}</p>
          )}
        </div>

        <Button type="submit" loading={loading} className="w-full">
          {loading
            ? t("auth.resetPassword.resetting")
            : t("auth.resetPassword.resetPassword")}
        </Button>
      </form>
    </div>
  );
};

export default ResetPassword;
